import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface ZTicket {
  id: number;
  user_id: number;
  user_name: string | null;
  user_email: string | null;
  subject: string;
  category: string;
  message: string | null;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
}

interface ZMsg {
  id: number;
  ticket_id: number;
  sender_type: string;
  sender_name: string | null;
  message: string;
  attachment_url: string | null;
  created_at: string;
}

type StatusFilter = 'all' | 'open' | 'in_progress' | 'resolved';

const STATUS_META: Record<string, { label: string; className: string; dot: string }> = {
  open: { label: 'Ouvert', className: 'bg-accent-50 text-accent-700', dot: 'bg-accent-500' },
  in_progress: { label: 'En cours', className: 'bg-primary-50 text-primary-700', dot: 'bg-primary-500' },
  resolved: { label: 'Résolu', className: 'bg-secondary-100 text-secondary-700', dot: 'bg-secondary-500' },
};

const CATEGORY_LABELS: Record<string, string> = {
  general: 'Question générale',
  bug: 'Problème',
  feedback: 'Retour / suggestion',
  billing: 'Abonnement & paiement',
  credit: 'Zifek Crédit',
};

function fmt(d: string) {
  return new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function SuperAdminSupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<ZTicket[]>([]);
  const [messages, setMessages] = useState<Record<number, ZMsg[]>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: tErr } = await supabase
        .from('zifek_tickets')
        .select('*')
        .order('updated_at', { ascending: false });
      if (tErr) throw tErr;
      const list = (data || []) as ZTicket[];
      setTickets(list);

      const ids = list.map((t) => t.id);
      const grouped: Record<number, ZMsg[]> = {};
      if (ids.length > 0) {
        const { data: msgData, error: mErr } = await supabase
          .from('zifek_ticket_messages')
          .select('*')
          .in('ticket_id', ids)
          .order('created_at', { ascending: true });
        if (mErr) throw mErr;
        (msgData || []).forEach((m) => {
          const msg = m as ZMsg;
          if (!grouped[msg.ticket_id]) grouped[msg.ticket_id] = [];
          grouped[msg.ticket_id].push(msg);
        });
      }
      setMessages(grouped);
      setSelectedId((prev) => (prev && list.some((t) => t.id === prev) ? prev : list[0]?.id ?? null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [selectedId, messages]);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      if (filter !== 'all' && t.status !== filter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          (t.subject || '').toLowerCase().includes(q) ||
          (t.user_name || '').toLowerCase().includes(q) ||
          (t.user_email || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [tickets, filter, search]);

  const counts = useMemo(() => ({
    all: tickets.length,
    open: tickets.filter((t) => t.status === 'open').length,
    in_progress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
  }), [tickets]);

  const selected = tickets.find((t) => t.id === selectedId) || null;
  const thread = selected ? messages[selected.id] || [] : [];

  const sendReply = async () => {
    if (!selectedId || !text.trim()) return;
    setSending(true);
    try {
      const { error: err } = await supabase.from('zifek_ticket_messages').insert({
        ticket_id: selectedId,
        sender_type: 'zifek',
        sender_name: user?.name || 'Équipe Zifek',
        message: text.trim(),
      });
      if (err) throw err;
      await supabase.from('zifek_tickets').update({ updated_at: new Date().toISOString(), status: 'in_progress' }).eq('id', selectedId);
      setText('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de l\u2019envoi');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status: string) => {
    if (!selectedId) return;
    const { error: err } = await supabase.from('zifek_tickets').update({ status, updated_at: new Date().toISOString() }).eq('id', selectedId);
    if (err) setError(err.message);
    else await load();
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            <i className="ri-customer-service-2-line mr-2 text-primary-500"></i>Support client
          </h2>
          <p className="text-sm text-foreground-500 mt-1">Demandes, plaintes et retours des commerçants Zifek</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..."
              className="pl-9 pr-3 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-full sm:w-56" />
          </div>
          <button onClick={load} title="Actualiser" className="w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors flex-shrink-0">
            <i className="ri-refresh-line"></i>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {([
          { key: 'all', label: 'Tous' },
          { key: 'open', label: 'Ouverts' },
          { key: 'in_progress', label: 'En cours' },
          { key: 'resolved', label: 'Résolus' },
        ] as const).map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${filter === f.key ? 'bg-foreground-950 text-background-50' : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'}`}>
            {f.label} · {counts[f.key]}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i><span className="flex-1">{error}</span>
          <button onClick={load} className="underline cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4 items-start">
          {/* Ticket list */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground-800">Tickets</span>
              <span className="text-xs text-foreground-400">{filtered.length}</span>
            </div>
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-500">Aucun ticket</p>
              </div>
            ) : (
              <div className="divide-y divide-background-200/70 max-h-[70vh] overflow-y-auto">
                {filtered.map((t) => {
                  const meta = STATUS_META[t.status] || STATUS_META.open;
                  const last = (messages[t.id] || []).slice(-1)[0];
                  return (
                    <button key={t.id} onClick={() => setSelectedId(t.id)}
                      className={`w-full text-left px-4 py-3 transition-colors cursor-pointer ${t.id === selectedId ? 'bg-primary-50' : 'hover:bg-background-50'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-foreground-900 truncate">{t.user_name || 'Commerçant'}</span>
                        <span className="text-[11px] text-foreground-400 whitespace-nowrap">{fmt(t.updated_at)}</span>
                      </div>
                      <p className="text-sm text-foreground-700 truncate mt-0.5">{t.subject}</p>
                      <p className="text-xs text-foreground-400 truncate mt-0.5">{last ? last.message : t.message}</p>
                      <span className={`inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.className}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>{meta.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Conversation */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            {selected ? (
              <div className="flex flex-col">
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-background-200/70">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-900 truncate">{selected.subject}</p>
                    <p className="text-xs text-foreground-500 truncate">
                      {selected.user_name} · {selected.user_email} · {CATEGORY_LABELS[selected.category] || selected.category}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => setStatus('in_progress')} title="Marquer en cours"
                      className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${selected.status === 'in_progress' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}>
                      En cours
                    </button>
                    <button onClick={() => setStatus('resolved')} title="Marquer résolu"
                      className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${selected.status === 'resolved' ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}>
                      Résolu
                    </button>
                    <button onClick={() => setStatus('open')} title="Réouvrir"
                      className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${selected.status === 'open' ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}>
                      Réouvrir
                    </button>
                  </div>
                </div>

                <div className="px-4 py-4 space-y-3 bg-background-50/50 overflow-y-auto" style={{ minHeight: '320px', maxHeight: '55vh' }}>
                  {thread.map((m) => {
                    const isZifek = m.sender_type === 'zifek';
                    return (
                      <div key={m.id} className={`flex ${isZifek ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[78%] flex flex-col ${isZifek ? 'items-end' : 'items-start'}`}>
                          <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isZifek ? 'bg-foreground-950 text-background-50 rounded-br-sm' : 'bg-background-100 text-foreground-800 rounded-bl-sm border border-background-200/70'}`}>
                            {m.message}
                          </div>
                          {m.attachment_url && (
                            <a href={m.attachment_url} target="_blank" rel="noopener noreferrer" className="mt-1.5 block w-40 rounded-lg overflow-hidden border border-background-200/70 cursor-pointer">
                              <img src={m.attachment_url} alt="Pièce jointe" className="w-full h-auto object-cover" />
                            </a>
                          )}
                          <span className="text-[11px] text-foreground-400 mt-1 px-1">
                            {isZifek ? (m.sender_name || 'Équipe Zifek') : (m.sender_name || selected.user_name || 'Commerçant')} · {fmt(m.created_at)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef}></div>
                </div>

                <div className="px-4 py-3 border-t border-background-200/70">
                  <div className="flex items-end gap-2">
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={1000}
                      placeholder="Répondre au commerçant… (Entrée pour envoyer)"
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      className="flex-1 px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300 resize-none" />
                    <button onClick={sendReply} disabled={sending || !text.trim()}
                      className="h-10 px-4 bg-primary-500 text-background-50 rounded-lg text-sm font-medium cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors flex items-center gap-1.5 whitespace-nowrap">
                      {sending ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-2-line"></i>}
                      <span className="hidden sm:inline">Répondre</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center px-6">
                <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-message-3-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-500">Sélectionnez un ticket pour voir la conversation.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}